import {
  AppBar, Toolbar, IconButton, Typography, Box, Avatar, Menu, MenuItem,
  Tooltip, Switch,
} from '@mui/material';
import {
  Menu as MenuIcon, DarkMode, LightMode, Logout, Person,
} from '@mui/icons-material';
import NotificationsPanel from '../common/NotificationsPanel';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toggleSidebar, toggleDarkMode, setLanguage } from '../../features/ui/uiSlice';
import { logout } from '../../features/auth/authSlice';
import api from '../../api/axios';

export default function Topbar({ impersonationOffset = 0 }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, refreshToken } = useSelector(s => s.auth);
  const { darkMode, language } = useSelector(s => s.ui);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleLogout = async () => {
    try { await api.post('/auth/logout', { refresh_token: refreshToken }); } catch {}
    dispatch(logout());
    navigate('/login');
  };

  const handleLangToggle = () => {
    const newLang = language === 'en' ? 'ur' : 'en';
    i18n.changeLanguage(newLang);
    dispatch(setLanguage(newLang));
  };

  return (
    <AppBar position="fixed" elevation={0} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', top: impersonationOffset }}>
      <Toolbar>
        <IconButton onClick={() => dispatch(toggleSidebar())} edge="start" sx={{ mr: 1 }}>
          <MenuIcon />
        </IconButton>

        <Box sx={{ flexGrow: 1 }} />

        {/* Language toggle */}
        <Tooltip title={language === 'en' ? 'اردو' : 'English'}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
            <Typography variant="caption" fontWeight={600} sx={{ mr: 0.5 }}>{language === 'en' ? 'EN' : 'اردو'}</Typography>
            <Switch size="small" checked={language === 'ur'} onChange={handleLangToggle} />
          </Box>
        </Tooltip>

        {/* Dark mode */}
        <Tooltip title={darkMode ? 'Light Mode' : 'Dark Mode'}>
          <IconButton onClick={() => dispatch(toggleDarkMode())} sx={{ mr: 1 }}>
            {darkMode ? <LightMode /> : <DarkMode />}
          </IconButton>
        </Tooltip>

        {/* Notifications */}
        <Box sx={{ mr: 1 }}>
          <NotificationsPanel />
        </Box>

        {/* User menu */}
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 13 }}>
            {user?.name?.[0]?.toUpperCase()}
          </Avatar>
        </IconButton>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', mb: 0.5 }}>
            <Typography variant="body2" fontWeight={700}>{user?.name}</Typography>
            <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
          </Box>
          <MenuItem onClick={() => { navigate('/profile'); setAnchorEl(null); }}>
            <Person fontSize="small" sx={{ mr: 1 }} /> My Profile
          </MenuItem>
          <MenuItem onClick={() => { navigate('/settings/subscription'); setAnchorEl(null); }}>
            Subscription
          </MenuItem>
          <MenuItem onClick={() => { navigate('/support'); setAnchorEl(null); }}>
            Support
          </MenuItem>
          <MenuItem onClick={handleLogout} sx={{ color: 'error.main', mt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Logout fontSize="small" sx={{ mr: 1 }} /> {t('auth.logout')}
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
